import React from 'react';
import { Wrench, TrendingUp, X } from 'lucide-react';

export default function OptimizationPanel({ isOpen, onClose, clubData, optimizationData, onApply }) {
  if (!isOpen || !optimizationData) return null;

  const formatValue = (value, unit = '') => {
    if (typeof value !== 'number' || isNaN(value)) return 'N/A';
    return `${value.toFixed(1)}${unit}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-in fade-in-50">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl w-full max-w-2xl text-white transform transition-all animate-in slide-in-from-bottom-10">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <Wrench className="text-blue-400" size={24} />
            <div>
              <h2 className="text-xl font-bold">Optimization for {clubData?.name}</h2>
              <p className="text-sm text-zinc-400">Based on your typical shot data.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-zinc-700">
            <X size={20} />
          </button>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {/* Gain Highlight */}
          <div className="text-center mb-6">
            <p className="text-zinc-400">Expected Gain</p>
            <p className="text-4xl font-bold text-green-400 flex items-center justify-center gap-2">
              <TrendingUp size={32} />
              +{formatValue(optimizationData.expected_gain_yards, ' yds')}
            </p>
            <p className="text-sm text-zinc-500">Total Distance</p>
          </div>

          {/* Comparison View */}
          <div className="grid grid-cols-2 gap-4">
            {/* Current Settings */}
            <div className="bg-zinc-800/50 p-4 rounded-lg">
              <h3 className="font-bold text-lg mb-3 text-center text-zinc-400">Current Settings</h3>
              <div className="text-center">
                <p className="text-sm text-zinc-500">Loft</p>
                <p className="text-3xl font-mono">{formatValue(optimizationData.current_loft, '°')}</p>
              </div>
            </div>

            {/* Recommended Settings */}
            <div className="bg-blue-500/10 border border-blue-400/30 p-4 rounded-lg">
              <h3 className="font-bold text-lg mb-3 text-center text-blue-300">Recommended</h3>
              <div className="text-center">
                <p className="text-sm text-blue-200/80">Loft</p>
                <p className="text-3xl font-mono text-white">{formatValue(optimizationData.recommended_loft, '°')}</p>
              </div>
            </div>
          </div>

          {/* Optimized Metrics */}
          <div className="mt-6">
             <h4 className="text-center font-semibold text-zinc-300 mb-3">Projected Performance</h4>
             <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                <MetricDisplay label="Launch Angle" value={formatValue(optimizationData.metrics.launch_angle, '°')} />
                <MetricDisplay label="Spin Rate" value={formatValue(optimizationData.metrics.spin_rate, 'rpm')} />
                <MetricDisplay label="Total Distance" value={formatValue(optimizationData.metrics.total_distance, 'yds')} />
                <MetricDisplay label="Descent Angle" value={formatValue(optimizationData.metrics.descent_angle, '°')} />
             </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-3 p-4 bg-zinc-900/50 border-t border-zinc-800">
          <button onClick={onClose} className="px-4 py-2 rounded-md bg-zinc-700 hover:bg-zinc-600 transition-colors">
            Close
          </button>
          <button 
            onClick={() => onApply(optimizationData.recommended_loft)} 
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 transition-colors font-semibold"
          >
            Apply Recommendation
          </button>
        </div>
      </div>
    </div>
  );
}

const MetricDisplay = ({ label, value }) => (
    <div className="bg-zinc-800 p-3 rounded-md">
        <p className="text-xs text-zinc-400">{label}</p>
        <p className="text-lg font-mono text-white">{value}</p>
    </div>
);
